import { DotsThreeIcon } from "@phosphor-icons/react/dist/csr/DotsThree";
import { memo, useMemo, useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { toast } from "@volca/ui/lib/toast";
import { dayjs, formatDate } from "@/core/utils/date";
import { getAttributedUserName } from "@/core/utils/name";
import { INote } from "@/customer/customer";
import { useCreateCustomerNote, useDeleteCustomerNote } from "@/customer/query";
import { ExpandableTextarea } from "./expandable-textarea";

const NoteDeleteDropdown = ({ onDelete }: { onDelete?: () => void }) => {
  const handleDelete = () => !!onDelete && onDelete();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Note actions"
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        }
      >
        <DotsThreeIcon />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-max"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {onDelete && (
          <DropdownMenuItem onClick={handleDelete}>
            Delete note
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface NoteItemProps {
  note: INote;
  customerId?: string;
  onDeleteNote?: (noteId: string) => void;
}

const NoteItem = memo(({ note, customerId, onDeleteNote }: NoteItemProps) => (
  <Stack className="w-full" align="start" gap={2}>
    <Stack
      align="start"
      gap={4}
      direction="row"
      justify="between"
      className="w-full items-center"
    >
      <Stack
        align="start"
        direction="row"
        gap={2}
        className="shrink-0 items-baseline"
      >
        <Text>{getAttributedUserName(note.user) || "Unknown user"}</Text>
        <Text variant="caption" tone="muted">
          {formatDate(note.created_at)}
        </Text>
      </Stack>
      {customerId && onDeleteNote && (
        <NoteDeleteDropdown onDelete={() => onDeleteNote(note.id)} />
      )}
    </Stack>
    <Stack className="w-full" align="start" gap={2}>
      <Text className="font-mdsm whitespace-pre-wrap">{note.content}</Text>
    </Stack>
  </Stack>
));

interface NoteListProps {
  notes: INote[];
  customerId?: string;
}

export const NoteList = ({ notes, customerId }: NoteListProps) => {
  const [noteText, setNoteText] = useState("");
  const textarearef = useRef<HTMLTextAreaElement>(null);
  const { mutate: addCustomerNote, isPending } = useCreateCustomerNote();
  const { mutate: deleteCustomerNote } = useDeleteCustomerNote();

  const sortedNotes = useMemo(
    () =>
      [...notes].sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at))),
    [notes],
  );

  const handleAddNote = () => {
    if (!customerId || !noteText.trim()) return;

    addCustomerNote(
      { customerId, content: noteText.trim() },
      {
        onSuccess: () => {
          toast.success("Note added successfully");
          setNoteText("");
          textarearef.current?.blur();
        },
        onError: (error: Error) => {
          toast.error("Failed to add note", {
            description:
              error instanceof Error ? error.message : "Please try again",
          });
        },
      },
    );
  };

  const handleDeleteNote = (noteId: string) => {
    if (!customerId) return;

    deleteCustomerNote(
      { customerId, noteId },
      {
        onSuccess: () => {
          toast.success("Note deleted successfully");
        },
        onError: (error: Error) => {
          toast.error("Failed to delete note", {
            description:
              error instanceof Error ? error.message : "Please try again",
          });
        },
      },
    );
  };

  return (
    <Stack align="start" className="h-full w-full" gap={0}>
      <div className="mt-4 mb-4 w-full">
        <Stack className="w-full" align="start" gap={4}>
          <ExpandableTextarea
            placeholder="New note"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            initialHeight="h-10"
            expandedHeight="h-36"
            className="resize-none"
            disabled={isPending || !customerId}
            onSubmit={noteText.trim() ? handleAddNote : undefined}
          />
        </Stack>
      </div>

      {notes.length === 0 ? (
        <Empty
          className={emptyStateVariants({
            layout: "inline",
            className: "px-6",
          })}
        >
          <EmptyHeader>
            <EmptyDescription>No notes for this customer</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="min-h-0 w-full flex-1">
          <div className="h-full overflow-y-auto pr-2">
            <div className="pb-6">
              <Stack className="w-full" align="start" gap={5}>
                {sortedNotes.map((note) => (
                  <NoteItem
                    key={note.id}
                    note={note}
                    customerId={customerId}
                    onDeleteNote={handleDeleteNote}
                  />
                ))}
              </Stack>
            </div>
          </div>
        </div>
      )}
    </Stack>
  );
};
